import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../App';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Clock } from 'lucide-react';
import { useSimulationJob } from '../hooks/useSimulationJob';
import { usePhotoConsent } from '../hooks/usePhotoConsent';
import { PhotoConsentDialog } from '../components/PhotoConsentDialog';
import { UploadCard } from '../components/simulation/UploadCard';
import { SimulationResult } from '../components/simulation/SimulationResult';

export default function SimulationPage() {
  const { language } = useLanguage();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { preview, loading, loadingSaved, progressStep, result, notFound, fileInputRef, selectFile, simulate, reset } =
    useSimulationJob(language, sessionId || null);
  const { consentOpen, onConsentOpenChange, onConsentAccepted, requireConsent, retentionDays } = usePhotoConsent();

  const handleReset = () => {
    reset();
    if (sessionId) navigate('/simulation');
  };

  const renderBody = () => {
    if (loadingSaved) {
      return (
        <div className="flex items-center justify-center gap-3 py-16 text-white/50 text-sm" data-testid="simulation-loading-saved">
          <Loader2 className="w-5 h-5 text-[var(--lux-gold)] animate-spin" /> {t('loadingSimulation', language)}
        </div>
      );
    }
    if (notFound) {
      return (
        <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="simulation-not-found">
          <CardContent className="py-12 text-center space-y-4">
            <Clock className="w-8 h-8 text-white/10 mx-auto" />
            <p className="text-white/40 text-sm">{t('simulationNotFound', language)}</p>
            <Button onClick={() => navigate('/history')} variant="outline" className="bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)]">
              {t('history', language)}
            </Button>
          </CardContent>
        </Card>
      );
    }
    if (result) return <SimulationResult result={result} language={language} onReset={handleReset} />;
    return (
      <UploadCard
        language={language}
        preview={preview}
        loading={loading}
        progressStep={progressStep}
        fileInputRef={fileInputRef}
        onFileSelect={selectFile}
        onReset={handleReset}
        onStart={() => requireConsent(simulate)}
      />
    );
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-5xl mx-auto" data-testid="simulation-page">
      <div className="fade-in">
        <h1 className="font-display text-3xl text-white">
          {language === 'fr' ? 'Simulation Avant / Apr\u00e8s' : 'Before / After Simulation'}
        </h1>
        <p className="text-white/50 text-sm mt-1">
          {language === 'fr'
            ? 'Uploadez votre photo et d\u00e9couvrez votre potentiel de transformation gr\u00e2ce \u00e0 l\'IA'
            : 'Upload your photo and discover your transformation potential with AI'}
        </p>
      </div>
      {renderBody()}
      <PhotoConsentDialog
        open={consentOpen}
        onOpenChange={onConsentOpenChange}
        onAccepted={onConsentAccepted}
        language={language}
        retentionDays={retentionDays}
      />
    </div>
  );
}
